import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Button, TextField, MenuItem, Switch, FormControlLabel, CircularProgress, FormControl, Select, OutlinedInput, Snackbar, Alert } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

function ImportConversion() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [campaigns, setCampaigns] = useState([]);
  const [partners, setPartners] = useState([]);
  const [eventOptions, setEventOptions] = useState([]);
  const [searchCampaign, setSearchCampaign] = useState('');
  const [searchPartner, setSearchPartner] = useState('');
  const [campaignPartners, setCampaignPartners] = useState([]);
  const [useNow, setUseNow] = useState(true);
  
  const [form, setForm] = useState({
    campaign_id: '', partner_id: '', event_id: 'cpl_base', conversion_date: new Date(),
    num_conversions: 1, custom_revenue: false, revenue: '', custom_payout: false, payout: ''
    // Note: revenue/payout always taken from DB by backend
  });

  useEffect(() => { loadData(); }, []);

  useEffect(() => { 
    if (form.campaign_id) {
      loadCampaignEvents(form.campaign_id);
      loadCampaignPartners(form.campaign_id);
    } else {
      setEventOptions([]);
      setCampaignPartners([]);
      setForm(prev => ({ ...prev, event_id: '', partner_id: '' }));
    }
  }, [form.campaign_id]);

  const loadData = async () => {
    try {
      const [campRes, partRes] = await Promise.all([axios.get('/admin/campaigns'), axios.get('/admin/partners')]);
      setCampaigns(Array.isArray(campRes.data) ? campRes.data : campRes.data?.data || []);
      setPartners(Array.isArray(partRes.data) ? partRes.data : partRes.data?.data || []);
    } catch (err) { console.error(err); }
  };

const loadCampaignEvents = async (campaignId) => {
    try {
      const res = await axios.get(`/admin/campaigns/${campaignId}`);
      const campaign = res.data;
      // Fetch correct revenue/payout from dedicated endpoint
      let baseRevenue = parseFloat(campaign.revenue) || parseFloat(campaign.payout) || 0;
      let basePayout = parseFloat(campaign.payout) || 0;
      try {
        const revRes = await axios.get(`/admin/campaigns/${campaignId}/revenue`);
        baseRevenue = parseFloat(revRes.data.revenue) || parseFloat(revRes.data.payout) || 0;
        basePayout = parseFloat(revRes.data.payout) || 0;
      } catch(e) {}
      const events = campaign.events || [];
      
      const options = [];
      options.push({
        id: 'cpl_base',
        name: 'CPL (Base)',
        revenue: baseRevenue,
        payout: basePayout,
        isDefault: true
      });
      
      events.forEach(ev => {
        const revenue = parseFloat(ev.revenue_amount) || 0;
        let payout = 0;
        if (ev.payout_type === 'PRV' && ev.payout_percentage) {
          payout = revenue * (parseFloat(ev.payout_percentage) / 100);
        } else {
          payout = parseFloat(ev.payout_amount) || 0;
        }
        options.push({
          id: String(ev.event_id),
          name: ev.event_name,
          revenue: revenue,
          payout: payout,
          isDefault: false
        });
      });
      
      setEventOptions(options);
      console.log("DEBUG revenue="+baseRevenue+" payout="+basePayout); setForm(prev => ({ ...prev, revenue: baseRevenue, payout: basePayout }));
    } catch (error) {
      console.error('Error loading events:', error);
      setEventOptions([{ id: 'cpl_base', name: 'CPL (Base)', revenue: 0, payout: 0, isDefault: true }]);
    }
  };

  const loadCampaignPartners = async (campaignId) => {
    try {
      const res = await axios.get(`/admin/campaigns/${campaignId}/partners`);
      const approvedPartners = (res.data.data || []).filter(p => p.status === 'active');
      setCampaignPartners(approvedPartners.map(p => p.partner_id));
    } catch (error) {
      console.error('Error loading campaign partners:', error);
      setCampaignPartners([]);
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (field === 'event_id' && value) {
      const ev = eventOptions.find(e => e.id === value);
      if (ev) setForm(prev => ({ ...prev, payout: ev.payout || '', revenue: ev.revenue || '' }));
    }
    // Pre-populate custom fields when toggling on
    if (field === 'custom_revenue' && value) {
      const ev = eventOptions.find(e => e.id === form.event_id);
      if (ev && !form.revenue) setForm(prev => ({ ...prev, revenue: ev.revenue || '' }));
    }
    if (field === 'custom_payout' && value) {
      const ev = eventOptions.find(e => e.id === form.event_id);
      if (ev && !form.payout) setForm(prev => ({ ...prev, payout: ev.payout || '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); 
    setLoading(true);
    try {
      const selectedEvent = eventOptions.find(ev => ev.id === form.event_id);
      const conversionDate = useNow 
        ? new Date().toISOString()
        : (form.conversion_date instanceof Date ? form.conversion_date.toISOString() : new Date(form.conversion_date).toISOString());
      
      await axios.post('/admin/conversions/import', {
        campaign_id: form.campaign_id, 
        partner_id: form.partner_id, 
        event_id: form.event_id === 'cpl_base' ? null : form.event_id,
        is_cpl_base: form.event_id === 'cpl_base',
        conversion_date: conversionDate, 
        num_conversions: parseInt(form.num_conversions),
        revenue: form.custom_revenue ? parseFloat(form.revenue) : (selectedEvent?.revenue || null),
        payout: form.custom_payout ? parseFloat(form.payout) : (selectedEvent?.payout || null)
      });
      
      setSnackbar({ open: true, message: `Successfully imported ${form.num_conversions} conversion(s)`, severity: 'success' });
      setTimeout(() => navigate('/conversions'), 1500);
    } catch (err) { 
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to import conversion', severity: 'error' });
      setLoading(false);
    }
  };

  const filteredCampaigns = campaigns.filter(c => c.name?.toLowerCase().includes(searchCampaign.toLowerCase()) && (searchCampaign || c.status === 'active')).sort((a, b) => (b.status === 'active') - (a.status === 'active'));
  const filteredPartners = partners.filter(p => {
    const matchesSearch = p.name?.toLowerCase().includes(searchPartner.toLowerCase());
    const isActive = p.status === 'active';
    const isInCampaign = campaignPartners.length === 0 || campaignPartners.includes(p.partner_id);
    return matchesSearch && (searchPartner || isActive) && isInCampaign;
  }).sort((a, b) => (b.status === 'active') - (a.status === 'active'));

  const labelStyle = { fontSize: '0.875rem', fontWeight: 600, color: 'text.secondary', mb: 0.5 };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ maxWidth: 800, mx: 'auto', mt: 2 }}>
        <Typography variant="h5" fontWeight={700} mb={3}>Add Conversion</Typography>
        
        <Paper sx={{ p: 3 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
            Fields with an asterisk (*) are mandatory.
          </Typography>
          
          <form onSubmit={handleSubmit}>
            {/* Row 1: Offer */}
            <Box sx={{ mb: 3 }}>
              <Typography sx={labelStyle}>Offer *</Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={form.campaign_id}
                  onChange={(e) => handleChange('campaign_id', e.target.value)}
                  displayEmpty
                  input={<OutlinedInput />}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 400 } } }}
                  renderValue={(selected) => {
                    if (!selected) return <span style={{ color: '#aaa' }}>Select Offer...</span>;
                    const camp = campaigns.find(c => String(c.campaign_id) === selected);
                    return camp?.name || selected;
                  }}
                >
                  <Box sx={{ p: 1, position: 'sticky', top: 0, bgcolor: 'white', zIndex: 1 }}>
                    <TextField size="small" fullWidth placeholder="Search..." value={searchCampaign} onChange={(e) => setSearchCampaign(e.target.value)} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} />
                  </Box>
                  {filteredCampaigns.map(c => (
                    <MenuItem key={c.campaign_id} value={String(c.campaign_id)}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: c.status === 'active' ? 'success.main' : 'grey.400' }} />
                        {c.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Row 2: Event + Partner */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Box sx={{ width: '50%' }}>
                <Typography sx={labelStyle}>Event *</Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={form.event_id}
                    onChange={(e) => handleChange('event_id', e.target.value)}
                    displayEmpty
                    input={<OutlinedInput />}
                    disabled={!form.campaign_id}
                    renderValue={(selected) => {
                      if (!selected) return <span style={{ color: '#aaa' }}>Select Event...</span>;
                      const ev = eventOptions.find(e => e.id === selected);
                      return ev ? ev.name : selected;
                    }}
                  >
                    {eventOptions.length === 0 ? (
                      <MenuItem value="" disabled>Select offer first</MenuItem>
                    ) : (
                      eventOptions.map(ev => (
                        <MenuItem key={ev.id} value={ev.id}>{ev.name}</MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ width: '50%' }}>
                <Typography sx={labelStyle}>Partner *</Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={form.partner_id}
                    onChange={(e) => handleChange('partner_id', e.target.value)}
                    displayEmpty
                    input={<OutlinedInput />}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 400 } } }}
                    renderValue={(selected) => {
                      if (!selected) return <span style={{ color: '#aaa' }}>Select Partner...</span>;
                      const part = partners.find(p => String(p.partner_id) === selected);
                      return part?.name || selected;
                    }}
                  >
                    <Box sx={{ p: 1, position: 'sticky', top: 0, bgcolor: 'white', zIndex: 1 }}>
                      <TextField size="small" fullWidth placeholder="Search..." value={searchPartner} onChange={(e) => setSearchPartner(e.target.value)} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} />
                    </Box>
                    {filteredPartners.map(p => (
                      <MenuItem key={p.partner_id} value={String(p.partner_id)}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: p.status === 'active' ? 'success.main' : 'grey.400' }} />
                          {p.name}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Row 3: Number of Conversions + Timestamp */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Box sx={{ width: '50%' }}>
                <Typography sx={labelStyle}>Number of Conversions</Typography>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={form.num_conversions}
                  onChange={(e) => handleChange('num_conversions', e.target.value)}
                  inputProps={{ min: 1 }}
                />
              </Box>
              <Box sx={{ width: '50%' }}>
                <Typography sx={labelStyle}>Timestamp</Typography>
                {useNow ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', height: 40 }}>
                    <FormControlLabel
                      control={<Switch checked={useNow} onChange={(e) => setUseNow(e.target.checked)} color="primary" />}
                      label="Now"
                    />
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControlLabel
                      control={<Switch checked={useNow} onChange={(e) => setUseNow(e.target.checked)} color="primary" />}
                      label=""
                      sx={{ mr: 0 }}
                    />
                    <DateTimePicker
                      value={form.conversion_date}
                      onChange={(date) => handleChange('conversion_date', date)}
                      slotProps={{ textField: { size: 'small', fullWidth: true } }}
                    />
                  </Box>
                )}
              </Box>
            </Box>

            {/* Row 4: Revenue + Payout */}
            <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
              <Box sx={{ width: '50%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography sx={{ ...labelStyle, mb: 0 }}>Revenue Amount per Conversion</Typography>
                  <FormControlLabel
                    control={<Switch checked={form.custom_revenue} onChange={(e) => handleChange('custom_revenue', e.target.checked)} size="small" />}
                    label={form.custom_revenue ? "Yes" : "No"}
                    labelPlacement="start"
                    sx={{ ml: 0, mr: 0 }}
                  />
                </Box>
                <TextField 
                  fullWidth 
                  size="small" 
                  type="number" 
                  value={form.revenue} 
                  onChange={(e) => handleChange('revenue', e.target.value)} 
                  placeholder="€"
                  disabled={!form.custom_revenue}
                  sx={{ visibility: form.custom_revenue ? 'visible' : 'hidden', height: form.custom_revenue ? 'auto' : 0 }}
                />
              </Box>
              <Box sx={{ width: '50%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography sx={{ ...labelStyle, mb: 0 }}>Payout Amount per Conversion</Typography>
                  <FormControlLabel
                    control={<Switch checked={form.custom_payout} onChange={(e) => handleChange('custom_payout', e.target.checked)} size="small" />}
                    label={form.custom_payout ? "Yes" : "No"}
                    labelPlacement="start"
                    sx={{ ml: 0, mr: 0 }}
                  />
                </Box>
                <TextField 
                  fullWidth 
                  size="small" 
                  type="number" 
                  value={form.payout} 
                  onChange={(e) => handleChange('payout', e.target.value)} 
                  placeholder="€"
                  disabled={!form.custom_payout}
                  sx={{ visibility: form.custom_payout ? 'visible' : 'hidden', height: form.custom_payout ? 'auto' : 0 }}
                />
              </Box>
            </Box>

            {/* Buttons */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button variant="outlined" onClick={() => navigate('/conversions')}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
                disabled={loading || !form.campaign_id || !form.partner_id || !form.event_id || (form.custom_revenue && !form.revenue) || (form.custom_payout && !form.payout)}
              >
                {loading ? 'Adding...' : 'Add'}
              </Button>
            </Box>
          </form>
        </Paper>

       <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            severity={snackbar.severity} 
            variant="filled" 
            sx={{ 
              width: '100%',
              backgroundColor: snackbar.severity === 'success' ? '#10B981' : undefined,
              color: 'white',
              '& .MuiAlert-icon': { color: 'white' }
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
}

export default ImportConversion;
// cache bust 1791449623
// v1791451881
// debug1791452098
